import {
  Box,
  Button,
  Heading,
  HStack,
  SimpleGrid,
  Stack,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { forwardRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Card from '../components/Card';
import Archive from '../components/icons/Archive';
import Printer from '../components/icons/Printer';
import Modal4BoxesTemplate from '../components/MyBox/Modal4BoxesTemplate';
import MyBox from '../components/MyBox/MyBox';
import useUserAuth from '../hooks/useUserAuth';
import { getGoalCategories } from '../stores/slices/goals/goalsActions';
import {
  setArchiveFilter,
  toggleArchive,
} from '../stores/slices/goals/goalsSlice';
import * as ics from 'ics';
import { getReminders } from '../stores/slices/reminders/remindersActions';
import { isIOSSafari } from '../utils/isIOSSafari';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaCaretDown } from 'react-icons/fa';
import { downloadIcsBlob } from '../utils/downloadIcsBlob';
import { securedAxiosInstance } from '../api/axios';
import { addDays, format, subSeconds } from 'date-fns';
import PdfBoxes from '../components/MyBox/PdfBoxes';
import { saveAs } from 'file-saver';
import { pdf } from '@react-pdf/renderer';
import { getCurrentUserInfo } from '../utils/getCurrentUserInfo';

const MyBoxes = () => {
  const CalendarCustomInput = forwardRef(({ value, onClick }, ref) => (
    <Button
      variant={'outline'}
      rightIcon={<FaCaretDown />}
      size={'sm'}
      onClick={onClick}
      ref={ref}
    >
      {value || 'Select date'}
    </Button>
  ));
  const [isDownloadingICS, setIsDownloadingICS] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const onChange = dates => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };
  const { userInfo, isAssumed, assumedUserInfo } = useSelector(state => state.user);
  const { categories, archived, filteredArchives } = useSelector(
    state => state.goals
  );
  const currentUser = getCurrentUserInfo({
    assumedUserInfo,
    isAssumed,
    userInfo,
  });
  const {
    isOpen: isOpenTemplate,
    onOpen: onOpenTemplate,
    onClose: onCloseTemplate,
  } = useDisclosure();
  const dispatch = useDispatch();

  const toast = useToast();
  useUserAuth();

  useEffect(() => {
    dispatch(getGoalCategories());
    if (archived) {
      dispatch(toggleArchive());
    }
  }, []);

  useEffect(() => {
    if (filteredArchives !== null) {
      setStartDate(filteredArchives.from);
      setEndDate(filteredArchives.to);
    }
  }, [filteredArchives]);

  const downloadICS = async () => {
    setIsDownloadingICS(true);
    const { payload } = await dispatch(getReminders());
    const reminders = payload.data.filter(res => res.goal_id !== null);
    if (reminders.length > 0) {
      const { error, value } = ics.createEvents(
        reminders.map(res => {
          const dueDate = new Date(res.due);
          return {
            start: [
              dueDate.getFullYear(),
              dueDate.getMonth() + 1,
              dueDate.getDate(),
            ],
            title: res.reminder,
            organizer: { name: userInfo.full_name, email: userInfo.email },
            attendees: res.users.map(user => {
              return {
                name: user.full_name,
                email: user.email,
              };
            }),
          };
        })
      );
      setIsDownloadingICS(false);
      if (error) {
        console.log(error);
        return;
      }

      let url = value;
      if (isIOSSafari()) {
        url = encodeURI(`data:text/calendar;charset=utf8,${value}`);
        window.open(url, '_blank');
        return;
      }

      downloadIcsBlob(url, '4boxes_reminders.ics');
    } else {
      setIsDownloadingICS(false);
      toast({
        description: 'No goal reminders found',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  const filterArchive = async () => {
    setIsFiltering(true);
    const res = await securedAxiosInstance.post(
      'goal_categories/archive/search',
      {
        from_date: new Date(format(startDate, 'yyyy-MM-dd')).toISOString(),
        to_date: subSeconds(
          addDays(new Date(format(endDate, 'yyyy-MM-dd')), 1),
          1
        ).toISOString(),
      }
    );
    setIsFiltering(false);
    dispatch(
      setArchiveFilter({ from: startDate, to: endDate, archives: res.data })
    );
  };

  const generatePdfDocument = async () => {
    setIsDownloadingPdf(true);
    const blob = await pdf(
      <PdfBoxes categories={categories} author={userInfo.full_name} />
    ).toBlob();
    saveAs(blob, `4boxes-${performance.now()}.pdf`);
    setIsDownloadingPdf(false);
  };

  return (
    <VStack px={{ base: 4, md: 10 }} py={8} spacing={{ base: 4, md: 8 }}>
      <Box w={'full'}>
        <Stack
          justifyContent={'space-between'}
          direction={{ base: 'column', lg: 'row' }}
          w={'full'}
          spacing={4}
        >
          <Heading>Hi {currentUser.full_name}, here are your 4Boxes!</Heading>
          <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
            <Button onClick={downloadICS} isLoading={isDownloadingICS}>
              Download all reminders
            </Button>
            {userInfo.role === 'admin' && (
              <Button onClick={onOpenTemplate}>Use Template</Button>
            )}
            <Button
              leftIcon={<Printer />}
              onClick={generatePdfDocument}
              isLoading={isDownloadingPdf}
            >
              Print
            </Button>
            <Button
              leftIcon={<Archive />}
              onClick={() => dispatch(toggleArchive())}
              bg={archived ? 'primaryDark' : 'primary'}
              _hover={{
                color: archived ? 'primaryDark' : 'primary',
                bg: 'primaryOpaque',
              }}
            >
              Archive
            </Button>
          </Stack>
        </Stack>
        {archived && (
          <Box w={'full'} mt={'4'}>
            <Heading as="h5" size="md" mb={'4'}>
              Archived Goals
            </Heading>
            <HStack>
              <Heading as="h6" size="sm">
                Filter date:
              </Heading>
              <DatePicker
                showPopperArrow={false}
                selected={startDate}
                onChange={onChange}
                startDate={startDate}
                endDate={endDate}
                className="react-datapicker__input-text"
                dateFormat="MMMM d, yyyy"
                customInput={<CalendarCustomInput />}
                selectsRange
              />
              <Button
                size={'sm'}
                onClick={filterArchive}
                disabled={startDate === null || endDate === null}
                isLoading={isFiltering}
              >
                Filter
              </Button>
              <Button
                variant={'ghost'}
                size={'sm'}
                disabled={startDate === null || endDate === null}
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  dispatch(setArchiveFilter(null));
                }}
              >
                Clear
              </Button>
            </HStack>
          </Box>
        )}
      </Box>
      <SimpleGrid
        columns={{ base: 1, lg: 2 }}
        spacingX={2}
        spacingY={{ base: 2, lg: 2 }}
        w={'full'}
      >
        {archived && filteredArchives !== null
          ? filteredArchives.archives?.length > 0 &&
            filteredArchives.archives.map(category => {
              return (
                <Card key={category.id} px={2}>
                  <MyBox category={category} />
                </Card>
              );
            })
          : categories?.length > 0 &&
            categories.map(category => {
              return (
                <Card key={category.id} px={2}>
                  <MyBox category={category} />
                </Card>
              );
            })}
      </SimpleGrid>
      <Modal4BoxesTemplate isOpen={isOpenTemplate} onClose={onCloseTemplate} />
    </VStack>
  );
};

export default MyBoxes;
